import { Box, Paper, Popper, Typography } from '@mui/material'
import { extent } from 'd3-array'
import { scaleLinear, scaleTime } from 'd3-scale'
import type { DateTime } from 'luxon'
import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { X_AXIS_HEIGHT, Y_AXIS_PADDING, Y_AXIS_WIDTH } from '@/features/bidding/components/cascading/charts/constants'
import GraphBackground from '@/features/bidding/components/cascading/charts/elements/GraphBackground'
import GraphHoverLine from '@/features/bidding/components/cascading/charts/elements/GraphHoverLine'
import GridLines from '@/features/bidding/components/cascading/charts/elements/GridLines'
import PriceLines from '@/features/bidding/components/cascading/charts/elements/PriceLines'
import QuantileBands from '@/features/bidding/components/cascading/charts/elements/QuantileBands'
import TimeAxis from '@/features/bidding/components/cascading/charts/elements/TimeAxis'
import ValueAxis from '@/features/bidding/components/cascading/charts/elements/ValueAxis'
import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import { useHourlyPriceData } from '@/features/bidding/hooks/useHourlyPriceData'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { ForecastMarketPair, PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import { getMarketProgramNames, isSymmetricMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import useResizeObserver from '@/hooks/useResizeObserver'

const LEFT_PADDING = 5
const DEFAULT_HEIGHT = 280

type TooltipState = {
  position: { x: number; y: number }
  hour: number
  prices: Array<{
    levelIndex: number
    direction: 'up' | 'down'
    price: number
  }>
} | null

type HourlyPriceChartProps = {
  bidLevelInputs: BidLevelInput[] | BidLevelInputSymmetrical[]
  selectedDeliveryDay: DateTime
  market: ForecastMarketPair
  pricingConfig?: PricingStrategyConfig
  forecastsData?: FormattedRow[]
  height?: number
}

function HourlyPriceChart({
  bidLevelInputs,
  selectedDeliveryDay,
  market,
  pricingConfig,
  forecastsData,
  height = DEFAULT_HEIGHT,
}: Readonly<HourlyPriceChartProps>) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const { width: observerWidth } = useResizeObserver(containerRef)
  const [tooltip, setTooltip] = useState<TooltipState>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const clipId = useId()

  const isSymmetric = isSymmetricMarket(market)
  const baseDate = useMemo(() => selectedDeliveryDay.startOf('day').toJSDate(), [selectedDeliveryDay])

  const { lines, datetimes, maxPrice, hasData, upQuantileBands, downQuantileBands } = useHourlyPriceData({
    levels: bidLevelInputs,
    market,
    baseDate,
    pricingConfig,
    forecastsData,
    selectedDeliveryDay,
  })

  const legendHeight = 24

  // Calculate dimensions - use minimum of 1 to avoid scale issues
  const containerWidth = Math.max(observerWidth - Y_AXIS_WIDTH - LEFT_PADDING, 1)
  const containerHeight = height - X_AXIS_HEIGHT - legendHeight

  // Create scales
  const timeScale = useMemo(() => {
    const [minDate, maxDate] = extent(datetimes)
    if (!minDate || !maxDate) {
      return scaleTime().domain([new Date(), new Date()]).range([0, containerWidth])
    }
    let endTime = maxDate
    if (datetimes.length >= 2) {
      const interval = datetimes[1].getTime() - datetimes[0].getTime()
      endTime = new Date(maxDate.getTime() + interval)
    }
    return scaleTime().domain([minDate, endTime]).range([0, containerWidth])
  }, [datetimes, containerWidth])

  const valueScale = useMemo(() => {
    const padding = maxPrice * 0.2
    const absMax = Math.max(maxPrice + padding, 1)

    if (isSymmetric) {
      // Positive-only scale for symmetrical markets
      return scaleLinear().domain([0, absMax]).range([containerHeight, Y_AXIS_PADDING])
    }

    // Symmetric scale around zero for directional markets
    return scaleLinear().domain([-absMax, absMax]).range([containerHeight, Y_AXIS_PADDING])
  }, [maxPrice, containerHeight, isSymmetric])

  // Handle hover
  const handleMouseMove = useCallback(
    (index: number) => {
      setHoveredIndex(index)

      if (!containerRef.current) return

      const hour = index
      const pricesForHour = lines
        .filter((line) => line.points.some((p) => p.hour === hour))
        .map((line) => {
          const point = line.points.find((p) => p.hour === hour)
          return {
            levelIndex: line.levelIndex,
            direction: line.direction,
            price: Math.abs(point?.price ?? 0), // Show absolute value in tooltip
          }
        })

      // Calculate position 20% past the edge of the hour
      const currentDatetime = datetimes[index]
      if (!currentDatetime) return

      const nextDatetime = datetimes[index + 1]
      let tooltipX = timeScale(currentDatetime)

      // Position at 120% of the interval (20% past the right edge)
      if (nextDatetime) {
        const interval = timeScale(nextDatetime) - timeScale(currentDatetime)
        tooltipX += interval * 1.2
      } else if (datetimes.length >= 2) {
        const estimatedInterval = timeScale(datetimes[1]) - timeScale(datetimes[0])
        tooltipX += estimatedInterval * 1.2
      }

      // Position at zero line
      const markerY = valueScale(0)

      // Convert to viewport coordinates
      const rect = containerRef.current.getBoundingClientRect()
      const viewportX = rect.left + LEFT_PADDING + tooltipX
      const viewportY = rect.top + markerY

      setTooltip({
        position: { x: viewportX, y: viewportY },
        hour,
        prices: pricesForHour,
      })
    },
    [lines, datetimes, timeScale, valueScale],
  )

  const handleMouseOut = useCallback(() => {
    setHoveredIndex(null)
    setTooltip(null)
  }, [])

  // Build dummy data point for GraphHoverLine compatibility
  const hoveredDataPoint: AreaGraphElement | null =
    hoveredIndex !== null
      ? {
          datetime: datetimes[hoveredIndex],
          meta: {},
          layerData: {},
        }
      : null

  const marketColor = getMarketColor(market)
  const marketLabel = getMarketProgramNames(market).displayName
  const hasQuantileBands = upQuantileBands !== null || downQuantileBands !== null

  // Memoize direction checks for legend rendering
  const { hasUpLines, hasDownLines } = useMemo(
    () => ({
      hasUpLines: lines.some((l) => l.direction === 'up'),
      hasDownLines: lines.some((l) => l.direction === 'down'),
    }),
    [lines],
  )

  return (
    <Box sx={{ width: '100%', height, px: 2 }}>
      {/* Legend header */}
      <Box
        sx={{
          height: legendHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Quantile band legend - only show when bands are available */}
          {hasQuantileBands && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: marketColor,
                  opacity: 0.15,
                }}
              />
              <span>P10-P90</span>
            </Box>
          )}
          {/* Direction legends */}
          {hasUpLines && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 2,
                  backgroundColor: '#16a34a',
                  borderRadius: 1,
                }}
              />
              <span>{t('bidding.bidding_cascading.charts.up_regulation')}</span>
            </Box>
          )}
          {hasDownLines && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 2,
                  backgroundColor: '#dc2626',
                  borderRadius: 1,
                }}
              />
              <span>{t('bidding.bidding_cascading.charts.down_regulation')}</span>
            </Box>
          )}
        </Box>
        <span>EUR/MWh</span>
      </Box>

      <div
        ref={containerRef}
        data-testid="hourly-price-chart"
        style={{ width: '100%', height: `${height - legendHeight}px` }}
      >
        {!hasData && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {t('bidding.bidding_cascading.charts.no_price_data')}
            </Typography>
          </Box>
        )}
        {hasData && observerWidth > 0 && (
          <svg height={`${height - legendHeight}px`} style={{ overflow: 'visible', width: '100%' }}>
            {/* Clip path to constrain quantile bands to plot area */}
            <defs>
              <clipPath id={clipId}>
                <rect height={containerHeight} width={containerWidth} x={0} y={0} />
              </clipPath>
            </defs>
            <g transform={`translate(${LEFT_PADDING}, 0)`}>
              {/* Grid lines (background) */}
              <GridLines scale={valueScale} width={containerWidth} />

              {/* Hover line (behind other elements) */}
              <GraphHoverLine
                hideMarker
                markerAtZero
                dataPoint={hoveredDataPoint}
                datetimes={datetimes}
                height={containerHeight}
                layerKeys={[]}
                markerFill={marketColor}
                selectedTimeIndex={hoveredIndex}
                timeScale={timeScale}
                valueScale={valueScale}
              />

              {/* Quantile bands (background) - clipped to plot area */}
              <g clipPath={`url(#${clipId})`}>
                <QuantileBands
                  color={marketColor}
                  datetimes={datetimes}
                  downBands={downQuantileBands}
                  timeScale={timeScale}
                  upBands={upQuantileBands}
                  valueScale={valueScale}
                />
              </g>

              {/* Zero line - only for directional markets */}
              {!isSymmetric && (
                <line
                  stroke="#e5e7eb"
                  strokeWidth={1}
                  x1={0}
                  x2={containerWidth}
                  y1={valueScale(0)}
                  y2={valueScale(0)}
                />
              )}

              {/* Price lines */}
              <PriceLines
                datetimes={datetimes}
                hoveredHour={hoveredIndex}
                lines={lines}
                timeScale={timeScale}
                valueScale={valueScale}
              />

              {/* Time axis */}
              <TimeAxis datetimes={datetimes} height={containerHeight} scale={timeScale} width={containerWidth} />

              {/* Value axis */}
              <ValueAxis
                formatTick={(v) => {
                  // For directional markets, show absolute value (both sides of zero)
                  // For symmetrical markets, show actual value
                  const displayValue = isSymmetric ? v : Math.abs(v)
                  return displayValue % 1 === 0 ? `${displayValue.toFixed(0)}` : `${displayValue.toFixed(2)}`
                }}
                scale={valueScale}
                width={containerWidth}
              />

              {/* Interaction layer */}
              <GraphBackground
                datetimes={datetimes}
                height={containerHeight}
                timeScale={timeScale}
                width={containerWidth}
                onMouseMove={handleMouseMove}
                onMouseOut={handleMouseOut}
              />
            </g>
          </svg>
        )}
      </div>

      {/* Tooltip */}
      <Popper
        anchorEl={
          tooltip
            ? {
                getBoundingClientRect: () => ({
                  top: tooltip.position.y,
                  left: tooltip.position.x,
                  bottom: tooltip.position.y,
                  right: tooltip.position.x,
                  width: 0,
                  height: 0,
                  x: tooltip.position.x,
                  y: tooltip.position.y,
                  toJSON: () => ({}),
                }),
              }
            : null
        }
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: ({ placement }: { placement: string }) => (placement === 'top-end' ? [-8, 8] : [8, 8]),
            },
          },
          { name: 'flip', options: { fallbackPlacements: ['top-end'] } },
        ]}
        open={tooltip !== null}
        placement="top-start"
        sx={{ zIndex: 1500, pointerEvents: 'none' }}
      >
        {tooltip && (
          <Paper
            elevation={0}
            sx={{
              p: 1,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 0.5,
                pb: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>
                {selectedDeliveryDay.toFormat('dd MMM yyyy')} {String(tooltip.hour).padStart(2, '0')}:00
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    backgroundColor: marketColor,
                  }}
                />
                <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>{marketLabel}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto', gap: 0.5 }}>
              {/* Group by level - reversed so L1 is at bottom, higher levels at top */}
              {Array.from(new Set(tooltip.prices.map((p) => p.levelIndex)))
                .sort((a, b) => b - a)
                .map((levelIndex) => {
                  const upPrice = tooltip.prices.find((p) => p.levelIndex === levelIndex && p.direction === 'up')
                  const downPrice = tooltip.prices.find((p) => p.levelIndex === levelIndex && p.direction === 'down')
                  return (
                    <Box key={levelIndex} sx={{ display: 'contents' }}>
                      <Typography sx={{ fontSize: '0.625rem', fontWeight: 600 }}>L{levelIndex + 1}</Typography>
                      <Typography sx={{ fontSize: '0.625rem', color: 'success.main' }}>
                        {upPrice ? `EUR ${upPrice.price.toFixed(2)}` : '-'}
                      </Typography>
                      {!isSymmetric && (
                        <Typography sx={{ fontSize: '0.625rem', color: 'error.main' }}>
                          {downPrice ? `EUR ${downPrice.price.toFixed(2)}` : '-'}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
            </Box>
          </Paper>
        )}
      </Popper>
    </Box>
  )
}

export default HourlyPriceChart
